import React, { useEffect, useRef, useState } from "react";
import { FocusScope, usePreventScroll } from "react-aria";

import type { MainNavBlock } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Themed } from "../../styles/themes";
import { Clickable } from "../Clickables";
import { Svg } from "../Svg";
import { useHeaderContext } from "./HeaderContext";
import { MobileSubmenu, MobileSubmenuCollapsedList } from "./MobileSubmenu";

import styles from "./MobileMenu.module.scss";

export const isSubmenu = (
    item: MainNavBlock[number],
): item is Extract<MainNavBlock[number], { type: "submenu" }> =>
    item.type === "submenu";

const LEVEL_DEPTH = { root: 0, submenu: 1, collapsedList: 2 } as const;
type Level = keyof typeof LEVEL_DEPTH;

const MobileMenuItem = ({
    item,
    isExpanded,
    onSubmenuTap,
}: {
    item: MainNavBlock[number];
    isExpanded: boolean;
    onSubmenuTap: (id: string) => void;
}) => {
    switch (item.type) {
        case "divider":
            return null;
        case "link":
            return (
                <li>
                    <Clickable
                        simpleLink={item.value}
                        className={styles.menuLink}
                    />
                </li>
            );
        case "submenu":
            return (
                <li>
                    <Clickable
                        className={concatClassNames([
                            styles.menuLink,
                            styles.submenuLink,
                        ])}
                        onPress={() => onSubmenuTap(item.id)}
                        aria-expanded={isExpanded}
                    >
                        <span>{item.value.title}</span>
                        <Svg name="caret-right" className={styles.caretIcon} />
                    </Clickable>
                </li>
            );
    }
};

export const MobileMenu = () => {
    const { headerContent, mobileMenuOpen, setMobileMenuOpen } =
        useHeaderContext();

    const [activeLevel, setActiveLevel] = useState<Level>("root");
    const [submenuId, setSubmenuId] = useState<string | null>(null);
    const [blockId, setBlockId] = useState<string | null>(null);

    usePreventScroll({ isDisabled: !mobileMenuOpen });

    useEffect(() => {
        if (!mobileMenuOpen) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- resets transient menu navigation state when the menu closes
            setActiveLevel("root");
            setSubmenuId(null);
            setBlockId(null);
        }
    }, [mobileMenuOpen]);

    const menuRef = useRef<HTMLDivElement>(null);

    /**
     * Mobile keyboard nav isn't as thorough as desktop - figure it's less
     * likely to matter.
     * Things mostly work with tabbing and entering, but one quirk was that when
     * I drilled into submenus, focus management got a little weird.
     * This effect fixes it: when activeLevel changes, focus on the first
     * focusable thing. This eliminated the quirks I saw.
     */
    useEffect(() => {
        if (!menuRef.current) return;
        const focusable = menuRef.current.querySelector<HTMLElement>(
            ":is(a, button):not([inert] *)",
        );
        focusable?.focus({ preventScroll: true });
    }, [activeLevel]);

    if (!mobileMenuOpen) return null;

    const mainNavItems = headerContent.main_nav.value;
    const topNavLinks = headerContent.top_nav.value;

    /**
     * When we render this component, each panel is compared against activeDepth
     * If we're at 1, for example, then 0 needs to animated off to the left and
     * 2 needs to be off to the right. If we're at 2, then both 0 and 1 need to
     * be off to the left. If we're at 0, 1 and 2 should be ready to animate in
     * from the right.
     */
    const panelClass = (level: Level) => {
        const depth = LEVEL_DEPTH[level];
        const activeDepth = LEVEL_DEPTH[activeLevel];
        return concatClassNames([
            styles.panel,
            depth < activeDepth ? styles.offLeft : "",
            depth > activeDepth ? styles.offRight : "",
        ]);
    };

    const activeSubmenu = submenuId
        ? mainNavItems.filter(isSubmenu).find((item) => item.id === submenuId)
        : null;

    const collapsedBlock =
        blockId && activeSubmenu && activeSubmenu.value.items
            ? activeSubmenu.value.items.find((block) => block.id === blockId)
            : null;

    return (
        <FocusScope contain restoreFocus autoFocus>
            <div
                ref={menuRef}
                id="mobile-menu"
                className={styles.menu}
                onKeyDown={(event) => {
                    if (event.key === "Escape") {
                        setMobileMenuOpen(false);
                    }
                }}
            >
                <Themed
                    theme="offWhite"
                    className={panelClass("root")}
                    inert={activeLevel !== "root" || undefined}
                >
                    <div className={styles.mainSection}>
                        <ul className={styles.menuList}>
                            {mainNavItems.map((item) => (
                                <MobileMenuItem
                                    key={item.id}
                                    item={item}
                                    isExpanded={
                                        submenuId === item.id &&
                                        activeLevel !== "root"
                                    }
                                    onSubmenuTap={(id) => {
                                        setSubmenuId(id);
                                        setBlockId(null);
                                        setActiveLevel("submenu");
                                    }}
                                />
                            ))}
                        </ul>
                    </div>
                    <Themed theme="black" className={styles.topNavSection}>
                        <ul className={styles.menuList}>
                            {topNavLinks.map((item) => (
                                <li key={item.id}>
                                    <Clickable
                                        simpleLink={item.value}
                                        className={styles.menuLink}
                                    />
                                </li>
                            ))}
                        </ul>
                    </Themed>
                </Themed>

                <Themed
                    theme="offWhite"
                    className={panelClass("submenu")}
                    inert={activeLevel !== "submenu" || undefined}
                >
                    {activeSubmenu && (
                        <MobileSubmenu
                            item={activeSubmenu}
                            expandedBlockId={
                                activeLevel === "collapsedList" ? blockId : null
                            }
                            onBack={() => setActiveLevel("root")}
                            onCollapsedListSelect={(blockId) => {
                                setBlockId(blockId);
                                setActiveLevel("collapsedList");
                            }}
                        />
                    )}
                </Themed>

                <Themed
                    theme="offWhite"
                    className={panelClass("collapsedList")}
                    inert={activeLevel !== "collapsedList" || undefined}
                >
                    {collapsedBlock && collapsedBlock.type !== "nav_ad" && (
                        <MobileSubmenuCollapsedList
                            block={collapsedBlock}
                            onBack={() => setActiveLevel("submenu")}
                        />
                    )}
                </Themed>
            </div>
        </FocusScope>
    );
};
